import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Subject, Subscription, catchError, debounceTime, distinctUntilChanged, of, switchMap, tap } from 'rxjs';
import {
  ClientOnboardingResult,
  ClientOrganizationsService,
  OrganizationOption,
} from '../../../../services/ClientOrganizationsService';

/** 'add' creates a new client; 'link' links an existing client that has no organization yet. */
export type ClientOrganizationDialogData =
  | { mode: 'add' }
  | { mode: 'link'; clientId: string; clientName: string };

// Matches the server: names need 3+ characters, but an organization id or Salesforce ID is searched whole.
const MIN_NAME_SEARCH = 3;
const EXACT_ID_PATTERN = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[A-Za-z0-9]{15}([A-Za-z0-9]{3})?)$/i;

@Component({
  selector: 'app-client-organization-dialog',
  standalone: true,
  templateUrl: './client-organization-dialog.component.html',
  styleUrls: ['./client-organization-dialog.component.css'],
  imports: [CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatProgressBarModule],
})
export class ClientOrganizationDialogComponent implements OnInit, OnDestroy {
  readonly isAdd: boolean;

  displayName = '';
  domain = '';
  salesforceId = '';

  searchText = '';
  results: OrganizationOption[] = [];
  searching = false;
  searchError: string | null = null;
  /** True once a search has returned, so "no matches" is only shown after one has actually run. */
  searched = false;

  /** The picked organization's id, or 'new' to create one. Null until the admin chooses. */
  selected: string | null = null;
  saving = false;

  // Until the admin types in the search box, it follows the client name so likely matches show up unprompted.
  private searchEdited = false;
  private readonly search$ = new Subject<string>();
  private sub?: Subscription;

  constructor(
    private service: ClientOrganizationsService,
    public dialogRef: MatDialogRef<ClientOrganizationDialogComponent, ClientOnboardingResult | undefined>,
    @Inject(MAT_DIALOG_DATA) public data: ClientOrganizationDialogData,
  ) {
    this.isAdd = data.mode === 'add';
    if (data.mode === 'link') this.displayName = data.clientName;
  }

  ngOnInit(): void {
    this.sub = this.search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => { this.searchError = null; }),
        switchMap(text => {
          if (!this.isSearchable(text)) {
            this.searching = false;
            this.searched = false;
            return of([] as OrganizationOption[]);
          }
          this.searching = true;
          return this.service.search(text).pipe(
            tap(() => { this.searched = true; }),
            catchError(err => {
              this.searchError = err?.message && !err?.status ? err.message : 'Search failed. Try again, or contact Well Chron support.';
              return of([] as OrganizationOption[]);
            }),
          );
        }),
      )
      .subscribe(results => {
        this.searching = false;
        this.results = results;
        // Drop a pick that is no longer on screen, so Save never sends an organization the admin cannot see.
        if (this.selected && this.selected !== 'new' && !results.some(r => r.organizationId === this.selected))
          this.selected = null;
      });

    if (!this.isAdd) {
      this.searchText = this.displayName;
      this.search$.next(this.searchText.trim());
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  get title(): string {
    return this.isAdd ? 'Add Client' : `Link '${this.displayName}' to an Enverus organization`;
  }

  get newOrganizationName(): string {
    return this.displayName.trim();
  }

  get canSave(): boolean {
    return !this.saving && !!this.displayName.trim() && !!this.selected;
  }

  isExactName(o: OrganizationOption): boolean {
    return o.name.trim().toLowerCase() === this.displayName.trim().toLowerCase();
  }

  onNameChange(): void {
    if (this.searchEdited) return;
    this.searchText = this.displayName;
    this.search$.next(this.searchText.trim());
  }

  onSearchChange(): void {
    this.searchEdited = true;
    this.search$.next(this.searchText.trim());
  }

  select(o: OrganizationOption): void {
    if (o.linkedClientId) return;
    this.selected = o.organizationId;
  }

  selectNew(): void {
    this.selected = 'new';
  }

  save(): void {
    if (!this.canSave) return;
    this.saving = true;

    const createOrganization = this.selected === 'new';
    const organizationId = createOrganization ? null : this.selected;
    const done = (r: ClientOnboardingResult) => this.dialogRef.close(r);
    const failed = () => { this.saving = false; };

    if (this.data.mode === 'add') {
      this.service.addClient({
        displayName: this.displayName.trim(),
        domain: this.domain.trim() || null,
        salesforceId: this.salesforceId.trim() || null,
        organizationId,
        createOrganization,
      }, done, failed);
    } else {
      this.service.linkOrganization({
        clientId: this.data.clientId,
        domain: this.domain.trim() || null,
        salesforceId: this.salesforceId.trim() || null,
        organizationId,
        createOrganization,
      }, done, failed);
    }
  }

  private isSearchable(text: string): boolean {
    return text.length >= MIN_NAME_SEARCH || EXACT_ID_PATTERN.test(text);
  }
}
